import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import controlIds from '../constants/control-ids';
import getColumnControlSettings from '../settings/column-control-settings';
import createSubmitTraitsColumnAppearanceHandler from '../helpers/create-submit-traits-column-appearance-handler';

const { Divider } = designSystem;
const {
  Context: ElementSettingsContext,
  components: {
    Section,
    ActionRadioGroupControl,
    ColorSchemeControl,
    ToggleControl,
    ToggleableSectionControl,
    SliderControl,
  },
  constants: { stages },
  helpers: { createBatch, generateInitialState },
  hooks: { useMountChunk },
} = elementSettings;

const EVENT_TRAITS_PREFIX = 'column';

const ColumnAppearanceSettingsContainer = ({ tabId, elementId, settingId }) => {
  const chunkId = tabId;
  const { callbacks, onUpdateState } = useContext(ElementSettingsContext);

  const controlSettings = useMemo(() => getColumnControlSettings(elementId), [elementId]);

  const handleUpdateState = useCallback(
    (batch) => {
      onUpdateState(batch, { chunkId });
    },
    [chunkId, onUpdateState]
  );

  const handleChange = useCallback(
    ({ controlId, elementIds, attributes, value, skipTracking = false }) => {
      const batch = createBatch({ elementIds, attributes, value });

      handleUpdateState(batch);
      callbacks.onControlClick({ controlId, settingId, skipTracking });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleToggleChange = useCallback(
    ({ controlId, elementIds, attributes, value }) => {
      const batch = createBatch({ elementIds, attributes, value });

      handleUpdateState(batch);
      callbacks.onToggleClick({ controlId, settingId, isActive: value });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleToggleableSectionChange = useCallback(
    ({ batch, controlId, value }) => {
      handleUpdateState(batch);
      callbacks.onToggleClick({ controlId, settingId, isActive: value });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const traitsHandler = useMemo(
    () => createSubmitTraitsColumnAppearanceHandler({ prefix: EVENT_TRAITS_PREFIX }),
    []
  );

  const { isChunkMounted } = useMountChunk(chunkId, {
    tabId,
    initialState: generateInitialState(Object.values(controlSettings)),
    stageHandlers: [
      {
        stage: stages.SUBMIT_TRAITS_GENERATION,
        handler: traitsHandler,
      },
    ],
  });

  if (!isChunkMounted) return null;

  return (
    <>
      <ColorSchemeControl {...controlSettings[controlIds.COLOR_SCHEME]} onChange={handleChange} />
      <Divider appearance="light-grey" />
      <Section title={i18next.t('Content alignment')}>
        <ActionRadioGroupControl
          {...controlSettings[controlIds.HORIZONTAL_CONTENT_ALIGNMENT]}
          nested
          onChange={handleChange}
        />
        <ActionRadioGroupControl
          {...controlSettings[controlIds.VERTICAL_CONTENT_ALIGNMENT]}
          nested
          onChange={handleChange}
        />
      </Section>
      <Divider appearance="light-grey" />
      <ToggleableSectionControl
        {...controlSettings[controlIds.SPACING_SECTION]}
        onChange={handleToggleableSectionChange}
      >
        <SliderControl {...controlSettings[controlIds.VERTICAL_SPACING]} onChange={handleChange} />
        <SliderControl
          {...controlSettings[controlIds.HORIZONTAL_SPACING]}
          onChange={handleChange}
        />
      </ToggleableSectionControl>
      <Divider appearance="light-grey" />
      <ToggleableSectionControl
        {...controlSettings[controlIds.CORNERS_SECTION]}
        onChange={handleToggleableSectionChange}
      >
        <SliderControl {...controlSettings[controlIds.CORNERS]} onChange={handleChange} />
      </ToggleableSectionControl>
      <Divider appearance="light-grey" />
      <ToggleControl {...controlSettings[controlIds.BORDER]} onChange={handleToggleChange} />
      <Divider appearance="light-grey" />
      <ToggleControl {...controlSettings[controlIds.SHADOW]} onChange={handleToggleChange} />
    </>
  );
};

ColumnAppearanceSettingsContainer.propTypes = {
  tabId: PropTypes.string.isRequired,
  elementId: PropTypes.string.isRequired,
  settingId: PropTypes.string.isRequired,
};

export default ColumnAppearanceSettingsContainer;
